import React, { useContext, useState, useRef, useEffect } from 'react';
import { WorkspaceContext } from '../../contexts/WorkspaceContext';
import authService from '../../auth/authService';
import ChannelItem from './ChannelItem';
import NewChannelModal from './NewChannelModal';
import './ChannelSection.css';

function ChannelSection({ selectedId, onSelect }) {
  const {
    channels,
    unreadCounts,
    archiveConversation,
    renameConversation,
    toggleAI,
    toggleFavorite,
    aiToggleEnabled
  } = useContext(WorkspaceContext);

  const [showModal, setShowModal] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const editRef = useRef(null);

  const isAdmin = authService.hasRole('system_admin') || authService.hasRole('admin');
  const currentUser = (authService.getEmail() || '').toLowerCase();

  useEffect(() => {
    if (editingId && editRef.current) {
      editRef.current.focus();
      editRef.current.select();
    }
  }, [editingId]);

  const startRename = (conv) => {
    setEditingId(conv.id);
    setEditValue(conv.title || '');
  };

  const commitRename = async (convId, currentTitle) => {
    if (isRenaming) return;
    const trimmed = editValue.trim();
    const originalTitle = (currentTitle || '').trim();
    if (!trimmed) {
      setEditingId(null);
      return;
    }
    if (trimmed === originalTitle) {
      setEditingId(null);
      return;
    }
    setIsRenaming(true);
    try {
      await renameConversation(convId, trimmed);
      setEditingId(null);
    } catch {
      // close edit mode so the UI never traps focus on failed rename attempts
      setEditingId(null);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleEditKeyDown = (e, convId, currentTitle) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitRename(convId, currentTitle);
    } else if (e.key === 'Escape') {
      setEditingId(null);
    }
  };

  const handleToggleAI = (conversationId) => {
    const conv = channels.find((channel) => channel.id === conversationId);
    if (conv?.aiEnabled) {
      if (!window.confirm('Disable AI assistance for this conversation?')) {
        return;
      }
    }
    toggleAI(conversationId);
  };

  return (
    <div className="channel-section">
      <div className="section-header">
        <button
          type="button"
          className="section-toggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
        >
          <svg
            className={`section-chevron ${collapsed ? 'collapsed' : ''}`}
            viewBox="0 0 16 16"
            width="10"
            height="10"
            fill="currentColor"
          >
            <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708l-3 3a.5.5 0 01-.708 0l-3-3a.5.5 0 010-.708z" />
          </svg>
          <span className="section-label">Channels</span>
        </button>
        <button
          className="section-add-btn"
          onClick={() => setShowModal(true)}
          aria-label="New channel"
          title="New channel"
        >
          +
        </button>
      </div>
      {!collapsed && (
        <div className="section-list">
          {channels.map((channel) => {
            if (editingId === channel.id) {
              return (
                <div
                  key={channel.id}
                  className={`channel-item-row ${selectedId === channel.id ? 'selected' : ''}`}
                >
                  <div className="channel-item" style={{ cursor: 'default' }}>
                    <span className="channel-hash">#</span>
                    <input
                      ref={editRef}
                      className="channel-edit-input"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleEditKeyDown(e, channel.id, channel.title || '')}
                      onBlur={() => commitRename(channel.id, channel.title || '')}
                    />
                  </div>
                </div>
              );
            }

            const isOwner = channel.creatorId === currentUser;
            const canToggleAI = aiToggleEnabled && (isAdmin || isOwner);

            return (
              <ChannelItem
                key={channel.id}
                conversation={channel}
                isSelected={selectedId === channel.id}
                onSelect={onSelect}
                unreadCount={unreadCounts[channel.id] || 0}
                onToggleFavorite={toggleFavorite}
                onToggleAI={canToggleAI ? handleToggleAI : undefined}
                onRename={isAdmin || isOwner ? startRename : undefined}
                onArchive={isAdmin ? archiveConversation : undefined}
                onLeave={!isOwner ? () => {} : undefined}
                isOwner={isOwner}
                isFavorited={false}
              />
            );
          })}
          {channels.length === 0 && (
            <span className="section-empty">No channels yet</span>
          )}
        </div>
      )}
      {showModal && <NewChannelModal onClose={() => setShowModal(false)} />}
    </div>
  );
}

export default ChannelSection;
